import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "md:flex gap-2 md:gap-4 my-2 md:my-4 bg-[#1F1F1F] rounded-md p-2 md:p-4"
}
const _hoisted_2 = { class: "grid grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 sm:gap-4 !z-10 w-full" }
const _hoisted_3 = { class: "relative min-w-[8rem] min-h-[8rem] aspect-square" }
const _hoisted_4 = ["href"]
const _hoisted_5 = ["src", "onLoad", "onError"]
const _hoisted_6 = {
  key: 1,
  class: "md:flex gap-2 md:gap-4 my-2 md:my-4 bg-[#1F1F1F] rounded-md p-2 md:p-4 text-white"
}
const _hoisted_7 = {
  key: 2,
  class: "bg-white rounded-md py-1 sm:py-2 px-2 sm:px-4"
}

import { Pagination } from 'ant-design-vue';
import { onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { Metadata } from '@/model/api';
import { Document, DocumentType } from '@/model/document';
import { Hitomi } from '@/model/hitomi';
import { DocumentViewMode } from '@/model/universal';
import { getDocumentPagination } from '@/repository/document';
import ImageCard from '@/components/shared/ImageCard.vue';

const imagePlaceholder =
	'https://www.signfix.com.au/wp-content/uploads/2017/09/placeholder-600x400.png';

export default /*#__PURE__*/_defineComponent({
  __name: 'HitomiGallery',
  props: {
    hitomi: {}
  },
  emits: ["loaded"],
  setup(__props: any, { emit }) {

const props = __props;





const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const images = ref<
	{
		url: string;
		isLoading: boolean;
		isError: boolean;
		stack: number;
		seq: number;
	}[]
>([]);
const documents = ref<Document[]>([]);
const stacks = ref<number[]>([]);
const store = useStore();
const code = ref('');

const metadata = ref<Metadata>(initialMetadata());
function initialMetadata(): Metadata {
	return {
		limit: 35,
		offset: 0,
		totalItem: 0,
		totalPage: 0,
		currentPage: Number(route.query.page) || 1
	};
}

onMounted(fetchDocument);
async function fetchDocument() {
	images.value = [];
	try {
		if (!props.hitomi) {
			throw Error('invalid data');
		}
		const nameSegments = props.hitomi.hitomiLink.split('-');
		code.value = nameSegments[nameSegments.length - 1].substring(
			0,
			nameSegments[nameSegments.length - 1].indexOf('.')
		);

		const res = await getDocumentPagination({
			documentType: DocumentType.Hitomi,
			limit: 1000,
			metadataID: code.value,
			sort: 'created_at asc'
		});
		if (res && !res.data) {
			throw Error('not found');
		}
		if (!res) {
			throw Error('invalid documents');
		}

		documents.value = res.data;
		metadata.value.totalItem = res.metadata.totalItem;
		metadata.value.totalPage = Math.ceil(res.metadata.totalItem / metadata.value.limit);
		stacks.value = res.data.map((_, index, arr) =>
			arr.slice(0, index).reduce((prev, cur) => prev + cur.files.length, 1)
		);
		setGallery();
	} catch (error) {
		if (!`${error}`.includes('not found')) {
			store.dispatch('alert', {
				type: 'error',
				message: 'something went wrong, please try again!'
			});
		}
	} finally {
		isLoading.value = false;
		emit('loaded');
	}
}

function setGallery() {
	if (metadata.value.currentPage > metadata.value.totalPage) {
		metadata.value.currentPage = 1;
	}
	router.replace({ query: { page: metadata.value.currentPage } });
	const first = (metadata.value.currentPage - 1) * metadata.value.limit;
	const last = Math.min(metadata.value.currentPage * metadata.value.limit, documents.value.length);
	images.value = documents.value.slice(first, last).map((data, index) => ({
		url: data.files[0].thumbnailURL,
		isLoading: true,
		isError: false,
		stack: data.files.length,
		seq: stacks.value[first + index]
	}));
}

watch(() => metadata.value.currentPage, setGallery);

return (_ctx: any,_cache: any) => {
  const _component_a_badge = _resolveComponent("a-badge")!
  const _component_a_empty = _resolveComponent("a-empty")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!isLoading.value && images.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(images.value, (image, index) => {
              return (_openBlock(), _createElementBlock("div", { key: index }, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(ImageCard, {
                    viewMode: _unref(DocumentViewMode).Stack,
                    isLoading: image.isLoading
                  }, null, 8, ["viewMode", "isLoading"]),
                  _createElementVNode("a", {
                    href: `/hitomi/reader/${_ctx.hitomi.hitomiUUID}/${code.value}#${image.seq}`,
                    target: "_blank"
                  }, [
                    _createElementVNode("img", {
                      alt: "",
                      src: image.isError ? imagePlaceholder : image.url,
                      onLoad: () => (image.isLoading = false),
                      onError: 
								() => {
									image.isLoading = false;
									image.isError = true;
								}
							,
                      class: _normalizeClass(
								(image.isLoading
									? 'w-0 h-0'
									: '!h-full !w-full cursor-pointer hover:brightness-75') +
								' object-cover rounded-md '
							),
                      loading: "lazy",
                      referrerPolicy: "no-referrer"
                    }, null, 42, _hoisted_5)
                  ], 8, _hoisted_4),
                  (!image.isError && !image.isLoading && image.stack > 1)
                    ? (_openBlock(), _createBlock(_component_a_badge, {
                        key: 0,
                        count: image.stack,
                        class: "absolute top-1 right-1 z-20"
                      }, null, 8, ["count"]))
                    : _createCommentVNode("", true)
                ])
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    (!isLoading.value && !images.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_component_a_empty, { class: "mt-2 sm:mt-4 mx-auto w-full" })
        ]))
      : _createCommentVNode("", true),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createVNode(_unref(Pagination), {
            current: metadata.value.currentPage,
            "onUpdate:current": _cache[0] || (_cache[0] = ($event: any) => ((metadata.value.currentPage) = $event)),
            "page-size": metadata.value.limit,
            "onUpdate:pageSize": _cache[1] || (_cache[1] = ($event: any) => ((metadata.value.limit) = $event)),
            total: metadata.value.totalItem,
            onChange: _cache[2] || (_cache[2] = (page) => (metadata.value.currentPage = page)),
            "show-size-changer": false
          }, null, 8, ["current", "page-size", "total"])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})